import { MoreHoriz as MoreIcon } from "@mui/icons-material";
import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
} from "@mui/material";
import React, { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { adminOrderItemService } from "../../../services/adminOrderItemService";
import { ItemStatus } from "../../../types/admin";
import { getItemStatusColor } from "../../../utils/statusColors";

interface StatusQuickMenuProps {
  itemId: string;
  currentStatusId?: number;
  currentStatusName?: string;
  currentStatusDisplayName?: string;
  onQuickStatusUpdate: (
    orderItemId: string,
    newStatusId: number,
    notes?: string,
    sendNotification?: boolean
  ) => Promise<void>;
  onOpenFullModal: () => void;
  getStatusColor: (status: string) => string;
}

// Simple cache for next-allowed statuses
const statusCache = new Map<number, { statuses: ItemStatus[]; timestamp: number }>();
const CACHE_TTL = 60000; // 1 minute

const StatusQuickMenu: React.FC<StatusQuickMenuProps> = ({
  itemId,
  currentStatusId,
  currentStatusName,
  currentStatusDisplayName,
  onQuickStatusUpdate,
  onOpenFullModal,
  getStatusColor,
}) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [nextStatuses, setNextStatuses] = useState<ItemStatus[]>([]);
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const menuOpen = Boolean(anchorEl);
  const loadedForStatusRef = useRef<number | undefined>(undefined);

  const loadNextStatuses = useCallback(async () => {
    if (!currentStatusId) return;

    // Check cache
    const cached = statusCache.get(currentStatusId);
    if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
      setNextStatuses(cached.statuses);
      return;
    }

    try {
      setLoading(true);
      const statuses = await adminOrderItemService.getNextAllowedStatuses(currentStatusId);
      setNextStatuses(statuses);
      statusCache.set(currentStatusId, { statuses, timestamp: Date.now() });
    } catch {
      setNextStatuses([]);
    } finally {
      setLoading(false);
    }
  }, [currentStatusId]);

  const handleChipClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    if (loadedForStatusRef.current !== currentStatusId) {
      loadNextStatuses();
      loadedForStatusRef.current = currentStatusId;
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleQuickUpdate = async (statusId: number) => {
    try {
      setUpdating(true);
      await onQuickStatusUpdate(itemId, statusId, undefined, true);
      handleClose();
      // Invalidate cache for current status since item moved
      if (currentStatusId) statusCache.delete(currentStatusId);
      loadedForStatusRef.current = undefined;
    } catch {
      // Error handled by parent
    } finally {
      setUpdating(false);
    }
  };

  const handleMoreOptions = () => {
    handleClose();
    onOpenFullModal();
  };

  return (
    <>
      <Tooltip title={t("admin.orders.clickToChangeStatus")}>
        <Chip
          label={currentStatusDisplayName || currentStatusName || t("admin.orders.pending")}
          size="small"
          color={getStatusColor(currentStatusName || "Pending") as any}
          onClick={handleChipClick}
          sx={{
            cursor: "pointer",
            "&:hover": { opacity: 0.8 },
          }}
        />
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleClose}
        onClick={(e) => e.stopPropagation()}
        slotProps={{
          paper: {
            sx: { minWidth: 200, maxWidth: 280 },
          },
        }}
      >
        {loading ? (
          <MenuItem disabled>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={16} />
              <ListItemText primary={t("common.loading")} />
            </Box>
          </MenuItem>
        ) : nextStatuses.length === 0 ? (
          <MenuItem disabled>
            <ListItemText
              primary={t("admin.orders.noNextStatuses")}
              primaryTypographyProps={{ variant: "body2", color: "text.secondary" }}
            />
          </MenuItem>
        ) : (
          nextStatuses.map((status) => (
            <MenuItem
              key={status.id}
              onClick={() => handleQuickUpdate(status.id)}
              disabled={updating}
            >
              <ListItemIcon sx={{ minWidth: 24 }}>
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    backgroundColor: getItemStatusColor(status),
                  }}
                />
              </ListItemIcon>
              <ListItemText primary={status.displayName} />
            </MenuItem>
          ))
        )}
        <Divider />
        <MenuItem onClick={handleMoreOptions} disabled={updating}>
          <ListItemIcon sx={{ minWidth: 24 }}>
            <MoreIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary={t("admin.orders.moreStatusOptions")}
            primaryTypographyProps={{ variant: "body2", fontStyle: "italic" }}
          />
        </MenuItem>
      </Menu>
    </>
  );
};

export default StatusQuickMenu;
